import type { FC } from "react";
import { useState } from "react";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import { useTranslation } from "@privacy/translations";
import { gridExpandedSortedRowEntriesSelector, useGridSelector } from "@mui/x-data-grid-premium";
import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { CircularProgress, HACK_alignIconWithLabelBox } from "@privacy/ui";
import {
  DownloadIcon,
  FileSpreadsheetIcon,
  FileXIcon,
  ChevronDownIcon,
  PDFIcon,
  TriangleAlertIcon,
} from "../shared/icons";
import { trackEvent } from "../shared/Snowplow";
import { useMutation } from "@tanstack/react-query";
import type { PDFReportsFormType } from "../api/reports";
import { requestPDFReportsMutationOptions } from "../api/reports";
import { useUserInfo } from "../api/userInfo";
import { useInitialisedGridApiRef } from "./DynamicGrid";
import { Grid } from "../shared/Grid";

type ExportButtonProps = {
  exportPdfConfig?: ExportPdfConfig;
};

export const ExportButton: FC<ExportButtonProps> = ({ exportPdfConfig }) => {
  const gridApiRef = useInitialisedGridApiRef();
  const { t } = useTranslation();
  const [buttonRef, setButtonRef] = useState<HTMLButtonElement | null>(null);

  const handleExport = (exportAction: () => void) => () => {
    exportAction();
    handleClose();
  };

  const handleClose = () => setButtonRef(null);
  const isOpen = Boolean(buttonRef);

  return (
    <>
      <Button
        onClick={e => setButtonRef(e.currentTarget)}
        variant="outlined"
        startIcon={<DownloadIcon size={14} />}
        endIcon={<ChevronDownIcon size={14} />}
      >
        <HACK_alignIconWithLabelBox>{t("RegistrationGrid.ExportButton")}</HACK_alignIconWithLabelBox>
      </Button>
      <Menu anchorEl={buttonRef} open={isOpen} onClose={handleClose}>
        <MenuItem
          onClick={() => {
            trackEvent.exportExcelClick();
            handleExport(gridApiRef.current.exportDataAsExcel)();
          }}
        >
          <FileSpreadsheetIcon />
          {t("RegistrationGrid.ExportExcel")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            trackEvent.exportCSVClick();
            handleExport(gridApiRef.current.exportDataAsCsv)();
          }}
        >
          <FileXIcon />
          {t("RegistrationGrid.ExportCSV")}
        </MenuItem>
        {exportPdfConfig && <ExportPdfMenuItem handleCloseMenu={handleClose} exportPdfConfig={exportPdfConfig} />}
      </Menu>
    </>
  );
};

type ExportPdfConfig = {
  formType: PDFReportsFormType;
  metadataFields: string[];
  excludedFields: Array<string>;
};

const ExportPdfMenuItem = ({
  handleCloseMenu,
  exportPdfConfig,
}: {
  handleCloseMenu: () => void;
  exportPdfConfig: ExportPdfConfig;
}) => {
  const { t } = useTranslation();
  const gridApiRef = useInitialisedGridApiRef();
  const exportPDFMutation = useMutation(requestPDFReportsMutationOptions());
  const rows = useGridSelector(gridApiRef, gridExpandedSortedRowEntriesSelector);

  const handleExportPDF = () => {
    const allColumns = gridApiRef.current.getAllColumns();
    const columnVisibilityModel = gridApiRef.current.state.columns?.columnVisibilityModel || {};
    const visibleColumnFields = allColumns
      .filter(({ field }) => columnVisibilityModel[field] !== false /* true if not explicitly hidden */)
      .map(({ field }) => field);

    const questionIds: string[] = [];
    const metadataFields: string[] = [];

    for (const field of visibleColumnFields) {
      if (field === Grid.ACTIONS_COLUMN_FIELD || exportPdfConfig.excludedFields.includes(field)) continue;

      if (exportPdfConfig.metadataFields.includes(field)) {
        metadataFields.push(field);
      } else {
        questionIds.push(field);
      }
    }

    // Get all row IDs (registration/system IDs etc.) - these are the responseIds
    const responseIds = rows.map(row => String(row.id));

    exportPDFMutation.mutate({
      filters: {
        questionIds,
        responseIds,
        metadataFields,
      },
      formType: exportPdfConfig.formType,
    });
  };

  return (
    <>
      <MenuItem
        onClick={() => {
          trackEvent.exportPDFClick(exportPdfConfig.formType);
          handleExportPDF();
        }}
        disabled={exportPDFMutation.isPending}
      >
        <PDFIcon />
        {t("RegistrationGrid.ExportPDF")}
      </MenuItem>
      {exportPDFMutation.status !== "idle" && (
        <ExportPdfStartedConfirmationDialog
          status={exportPDFMutation.status}
          handleClose={() => {
            exportPDFMutation.reset();
            handleCloseMenu();
          }}
        />
      )}
    </>
  );
};

const CONTENT_HEIGHT = "120px";
const ExportPdfStartedConfirmationDialog = ({
  handleClose,
  status,
}: {
  handleClose: () => void;
  status: "success" | "pending" | "error";
}) => {
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  return (
    <Dialog open onClose={handleClose} slotProps={{ paper: { sx: { width: "min(575px, 100%)" } } }}>
      <DialogTitle>{t("Download.Reports.PDF.Dialog.Header")}</DialogTitle>
      <DialogContent>
        {(() => {
          if (status === "pending") {
            return (
              <Stack sx={{ justifyContent: "center", alignItems: "center", height: CONTENT_HEIGHT }}>
                <CircularProgress size={24} />
              </Stack>
            );
          }
          if (status === "success") {
            return (
              <Stack spacing={4} sx={{ height: CONTENT_HEIGHT }}>
                <Typography
                  sx={{ color: theme => theme.palette.text.tertiary, fontSize: theme => theme.typography.pxToRem(16) }}
                >
                  {t("Download.Reports.PDF.Dialog.Content")}{" "}
                  <Typography component="span" sx={{ fontWeight: "bold" }}>
                    {userInfo.email}
                  </Typography>{" "}
                  {t("Download.Reports.PDF.Dialog.Content2")}
                </Typography>
              </Stack>
            );
          }
          if (status === "error") {
            return (
              <Stack
                direction="row"
                sx={{ height: CONTENT_HEIGHT, alignItems: "center", gap: 1, justifyContent: "center" }}
              >
                <TriangleAlertIcon size={16} color={theme => theme.palette.error.main} />
                <Typography sx={theme => ({ fontSize: theme.typography.pxToRem(16), color: theme.palette.error.main })}>
                  {t("Download.Reports.PDF.Dialog.Error")}
                </Typography>
              </Stack>
            );
          }
        })()}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={() => handleClose()}>
          {t("Download.Reports.PDF.Dialog.CloseButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
